import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from '@mui/material';

// Shared cell borders so the header and line-item tables read as one grid.
const borderedCells = {
  '& td, & th': {
    border: '1px solid',
    borderColor: 'divider',
    py: 0.9,
    px: 1.5,
  },
};

// A value longer than this gets a wider cell so addresses and notes do not
// wrap into a tall sliver next to mostly-empty neighbours.
const WIDE_VALUE_LENGTH = 45;

/**
 * Header / vendor details as a responsive grid of bordered cells: bold label on
 * top, value underneath. Cells flow to fill the available width and wrap to the
 * next row, so a document with many fields stays compact instead of turning into
 * one very long two-column list.
 *
 * The 1px gap over a divider-coloured background draws the grid lines, which
 * avoids the doubled borders you get from bordering each cell individually.
 */
const DetailGrid = ({ rows }) => (
  <Box
    sx={(theme) => ({
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(auto-fill, minmax(210px, 1fr))',
      },
      gap: '1px',
      backgroundColor: theme.palette.divider,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      overflow: 'hidden',
    })}
  >
    {Object.entries(rows).map(([key, value]) => {
      const text = value === null || value === undefined ? '' : String(value).trim();
      const display = text === '' || text === 'NaN' ? '—' : text;
      const wide = display.length > WIDE_VALUE_LENGTH;
      return (
        <Box
          key={key}
          sx={{
            px: 1.5,
            py: 1,
            backgroundColor: 'background.paper',
            gridColumn: { xs: 'auto', sm: wide ? 'span 2' : 'auto' },
            minWidth: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 700,
              textTransform: 'capitalize',
              color: 'text.secondary',
              lineHeight: 1.4,
            }}
          >
            {key.replace(/_/g, ' ')}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {display}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const DocumentDataPopup = ({ open, onClose, data, onSubmit, documentType = 'VendorInvoice' }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  if (!data) return null;

  // ---- Normalize data depending on documentType ----
  let header_details = {};
  let vendor_details = {};
  let item_details = [];

  if (documentType === 'SalesOrder') {
    // Sales Order → array structure from /process-so
    const soData = Array.isArray(data) ? data[0] : data;
    header_details = soData?.header_details || {};
    item_details = soData?.item_details || [];
  } else {
    // Invoice / PO / GRN structure.
    // /process-miro-invoice and /process-fb-invoice wrap the result in
    // "extracted_data"; /process-grn and /process-quotation return the extracted
    // JSON at the top level. Accept both, otherwise those popups render empty.
    const extracted_data = data.extracted_data || data;
    const isMiro = extracted_data?.header_details?.supplier_name !== undefined;

    header_details = extracted_data?.header_details || {};
    vendor_details = isMiro ? {} : extracted_data?.vendor_details || {};
    item_details = extracted_data?.item_details || [];
  }

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit();
    } catch (error) {
      console.error('Error submitting document:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Title ----
  const titleMap = {
    VendorInvoice: 'Processed Invoice Summary',
    PurchaseOrder: 'Processed Purchase Order',
    GRN: 'Processed GRN Details',
    SalesOrder: 'Sales Order Details (Generated from PO)',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth  PaperProps={{
    sx: { maxHeight: '80vh'}
  }}>
      <DialogTitle>{titleMap[documentType] || 'Document Summary'}</DialogTitle>

      <DialogContent dividers 
      sx={{
        maxHeight: '65vh',
        overflowY: 'auto',
      }}>
        {/* Header Details */}
{/* Header + Vendor Details side-by-side layout */}
{/* Header + Vendor Details */}
{documentType !== 'SalesOrder' && (() => {
  const hasVendor = vendor_details && Object.keys(vendor_details).length > 0;
  return (
    // Plain flex rather than <Grid>: this project is on MUI v7, where Grid takes
    // `size={{...}}` and the legacy `item`/`xs`/`md` props are silently ignored -
    // which left these blocks with no width, shrinking them to about half the
    // dialog and capping DetailGrid at two columns.
    <Box
      mb={3}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}
    >
      {/* Header Details - full width on its own, shares the row with Vendor Details */}
      <Box sx={{ flex: hasVendor ? '1 1 420px' : '1 1 100%', minWidth: 0 }}>
        <Typography variant="h6" gutterBottom>
          Header Details
        </Typography>
        <DetailGrid rows={header_details} />
      </Box>

      {/* Vendor Details - only show if present */}
      {hasVendor && (
        <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
          <Typography variant="h6" gutterBottom>
            Vendor Details
          </Typography>
          <DetailGrid rows={vendor_details} />
        </Box>
      )}
    </Box>
  );
})()}


        {/* Line Items */}
{item_details && item_details.length > 0 && (
  <Box sx={{ mt: 4, pt: 1 }}>
    {/* Only show title for non-SalesOrder documents */}
    {documentType !== 'SalesOrder' && (
      <Typography
        variant="h6"
        gutterBottom
        sx={{ mb: 2 }}
      >
        Line Items
      </Typography>
    )}

    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader sx={borderedCells}>
        <TableHead
          sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[200],
          })}
        >
          <TableRow>
            {Object.keys(item_details[0])
              .filter((key) => documentType === 'SalesOrder' ? key !== 'MaterialAl' : true)
              .map((key) => (
                <TableCell
                  key={key}
                  sx={(theme) => ({
                    fontWeight: 'bold',
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                    // stickyHeader positions the cells, so the background has to
                    // sit on them or scrolled rows show through the header.
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[900]
                        : theme.palette.grey[200],
                  })}
                >
                  {key.replace(/_/g, ' ')}
                </TableCell>
              ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {item_details.map((item, idx) => (
            <TableRow key={idx}>
              {Object.entries(item)
                .filter(([key]) => documentType === 'SalesOrder' ? key !== 'MaterialAl' : true)
                .map(([key, val], i) => (
                  <TableCell key={i}>{String(val)}</TableCell>
                ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
)}


      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Submit to SAP'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentDataPopup;
